<script setup lang="ts">
import {
  PhArrowLeft as ArrowLeft,
  PhArrowRight as ArrowRight,
  PhCheck as Check,
  PhCopy as Copy,
  PhDownloadSimple as DownloadSimple,
} from "@phosphor-icons/vue";
import { toPng } from "html-to-image";
import { computed, nextTick, ref } from "vue";
import BrandBar from "./BrandBar.vue";
import HomeButton from "./HomeButton.vue";
import WorkhorseIdentityCard from "./WorkhorseIdentityCard.vue";
import { results, workhorseIdentities } from "../data/content";
import { useSessionStore } from "../stores/session";

const store = useSessionStore();
const card = ref<HTMLElement>();
const qrCode = "/assets/nbti-poster-qr.png";
const working = ref(false);
const exporting = ref(false);
const definition = computed(() => results[store.result!.resultCode]);
const workhorseIdentity = computed(() => workhorseIdentities[store.result!.resultCode]);
const roleTheme = computed(() => ({
  "--poster-accent": definition.value.poster.accent,
  "--poster-soft": definition.value.poster.soft,
  "--poster-foreground": definition.value.poster.foreground,
}));

async function waitForPosterAssets() {
  if (document.fonts?.ready) await document.fonts.ready;
  if (!card.value) return;
  const images = Array.from(card.value.querySelectorAll("img"));
  await Promise.all(images.map(async (image) => {
    if (!image.complete) {
      await new Promise<void>((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error(`image timed out: ${image.src}`)), 12000);
        const cleanup = () => window.clearTimeout(timeout);
        image.addEventListener("load", () => { cleanup(); resolve(); }, { once: true });
        image.addEventListener("error", () => { cleanup(); reject(new Error(`image failed: ${image.src}`)); }, { once: true });
      });
    }
    if (image.naturalWidth <= 0) throw new Error(`image unavailable: ${image.src}`);
    if (typeof image.decode === "function") await image.decode();
  }));
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

async function downloadPoster() {
  if (!card.value || working.value) return;
  working.value = true;
  try {
    exporting.value = true;
    await nextTick();
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await waitForPosterAssets();
    const exportWidth = 360;
    const exportHeight = Math.ceil(card.value.scrollHeight);
    const exportOptions = {
      width: exportWidth,
      height: exportHeight,
      pixelRatio: 3,
      cacheBust: false,
      backgroundColor: "#ffffff",
      style: {
        width: `${exportWidth}px`,
        height: `${exportHeight}px`,
        maxWidth: "none",
      },
    };
    await toPng(card.value, { ...exportOptions, pixelRatio: 1 });
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    const dataUrl = await toPng(card.value, exportOptions);
    const link = document.createElement("a");
    link.download = `NBTI-${definition.value.code}-牛马身份证.png`;
    link.href = dataUrl;
    link.click();
    store.track("poster_generate", { result_code: definition.value.code, status: "success" });
    store.toast("海报已生成");
  } catch {
    store.track("poster_generate", { result_code: definition.value.code, status: "failed" });
    store.toast("生成失败，可以先截图保存");
  } finally {
    exporting.value = false;
    working.value = false;
  }
}

async function share() {
  const url = window.location.href;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      const input = document.createElement("textarea");
      input.value = url;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand("copy");
      input.remove();
      if (!copied) throw new Error("copy failed");
    }
    store.track("link_copy", { result_code: definition.value.code, status: "success" });
    store.toast("链接复制成功");
  } catch {
    store.track("link_copy", { result_code: definition.value.code, status: "failed" });
    store.toast("复制失败，请长按地址栏复制");
  }
}

function continueToRecovery() {
  store.go("state");
}
</script>

<template>
  <section class="screen screen--poster poster-redesign" :style="roleTheme">
    <header class="subpage-header">
      <button class="icon-button" aria-label="返回结果" @click="store.go('result')"><ArrowLeft :size="22" weight="bold" /></button>
      <BrandBar compact label="牛马身份海报" />
      <HomeButton />
    </header>

    <p class="eyebrow poster-eyebrow">保存图片或复制链接，发给懂你的人</p>

    <article ref="card" class="share-poster share-poster--social" :class="{ 'share-poster--exporting': exporting }" :style="roleTheme">
      <div class="social-poster">
        <header class="social-poster__header">
          <div><strong>NBTI</strong><span>牛马体质鉴定所</span></div>
          <div class="social-poster__level"><small>班味指数</small><strong>{{ definition.banweiLabel }}</strong></div>
        </header>
        <h1>我是<span>{{ definition.name }}</span></h1>
        <blockquote>{{ definition.shareText }}</blockquote>
        <div class="social-poster__visual"><img :src="definition.poster.image" :alt="definition.name" /></div>
        <WorkhorseIdentityCard :identity="workhorseIdentity" />
        <footer class="social-poster__footer">
          <div><strong>测测你是什么牛马</strong><small>5 道题 · 扫码立即测试</small></div>
          <img :src="qrCode" alt="NBTI 测试二维码" />
        </footer>
      </div>
    </article>

    <div class="poster-actions">
      <button class="button button--primary" :disabled="working" @click="downloadPoster"><DownloadSimple :size="21" weight="bold" /> {{ working ? "正在生成…" : "保存海报到相册" }}</button>
      <button class="button button--soft" @click="share"><Copy :size="21" weight="bold" /> 复制链接</button>
      <button class="button result-recovery-cta poster-return-cta" @click="continueToRecovery">继续看看我的工位回血路线 <ArrowRight :size="20" weight="bold" /></button>
      <p><Check :size="16" weight="bold" /> 海报不会展示你的答案和健康信息</p>
      <button class="text-button" @click="store.restart"><Copy :size="17" /> 重新测一次</button>
    </div>
  </section>
</template>
